import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useSearchParams,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { PDFDownloadLink } from "@react-pdf/renderer";
import {
  Sparkles,
  ClipboardList,
  Target,
  TrendingUp,
  Package,
  CheckCircle2,
  AlertCircle,
  Download,
  ChevronLeft,
} from "lucide-react";
import { Card, CardContent } from "../../components/Card";
import { EmptyStateContainer } from "../../components/EmptyStateContainer";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import apiClient from "../../api-client";
import LeadmagnetPDF from "./LeadmagnetPDF";
import { normalizePackRecommendation } from "./packCatalog";

const Leadmagnet = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const { setMarketingPlanRefresh, setPageContext } = outletContext;
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [strategyData, setStrategyData] = useState(null);
  const [status, setStatus] = useState(null);
  const [tenantId, setTenantId] = useState(null);

  // Estados para el formulario
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    empresa: "",
    website: "",
  });
  const [formErrors, setFormErrors] = useState({
    nombre: "",
    email: "",
    telefono: "",
    empresa: "",
    website: "",
  });
  const [submittingLead, setSubmittingLead] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const pdfLinkRef = useRef(null);
  const normalizedPack = useMemo(
    () => normalizePackRecommendation(strategyData?.pack_recomendado),
    [strategyData]
  );

  const uuii = searchParams.get("uuii") || params.uuii;
  const hideForm = searchParams.get("hideForm") === "true";
  const returnTo = searchParams.get("returnTo");
  const backPath =
    returnTo === "plan-marketing" ? "/HerramientasIA/planes" : "/leads";
  const backLabel =
    returnTo === "plan-marketing" ? "Volver a Planes de Marketing" : "Volver a Leads";
  const isEmbeddedInApp = Boolean(setPageContext);

  const handleBackNavigation = React.useCallback(() => {
    if (returnTo === "plan-marketing" && typeof setMarketingPlanRefresh === "function") {
      setMarketingPlanRefresh((prev) => prev + 1);
    }
    navigate(backPath);
  }, [returnTo, setMarketingPlanRefresh, navigate, backPath]);

  React.useEffect(() => {
    if (!setPageContext) return;

    setPageContext({
      title: "Plan de Marketing",
      showTitle: true,
      leftContent: hideForm ? (
        <IconButton
          onClick={handleBackNavigation}
          size="small"
          sx={{
            color: "primary.main",
            padding: "4px",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <ChevronLeft size={20} />
        </IconButton>
      ) : null,
    });
  }, [setPageContext, hideForm, handleBackNavigation]);

  const pdfPalette = useMemo(() => {
    const divider = theme.palette.divider || "#e0e0e0";

    return {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryWash: "#faf9fe", // Ultra subtle purple (matches web 8% opacity)
      success: theme.palette.success.main,
      successLight: theme.palette.success.light,
      successWash: "#f8fdfb", // Ultra subtle green
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: theme.palette.background.default,
      backgroundLight: theme.palette.background.paper,
      surface: theme.palette.background.paper,
      cardBorder: divider,
    };
  }, [theme]);

  // Ref para el polling interval
  const pollingIntervalRef = useRef(null);

  // Fetch inicial
  useEffect(() => {
    const fetchStrategy = async () => {
      if (!uuii) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/leadmagnet/strategy/${uuii}`);
        const data = response.data;

        // Guardar el tenant_id si viene en la respuesta
        if (data.byneural_tenant_id) {
          setTenantId(data.byneural_tenant_id);
        }

        if (data.status === "completed" && data.json_result) {
          setStrategyData(data.json_result);
          setStatus("completed");
          setLoading(false);
          // Limpiar polling si existe
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else if (data.status === "processing") {
          setStatus("processing");
          setLoading(true);
        } else if (data.status === "failed") {
          setError(data.error_message || "Error al generar la estrategia");
          setStatus("failed");
          setLoading(false);
          // Limpiar polling si existe
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error("Error fetching strategy:", err);
        setError(
          "No se pudo cargar tu plan de marketing. Por favor, intenta nuevamente."
        );
        setLoading(false);
        // Limpiar polling si existe
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };

    // Fetch inicial
    fetchStrategy();

    // Cleanup al desmontar
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [uuii]);

  // Efecto separado para el polling
  useEffect(() => {
    if (!uuii) return;

    const startPolling = () => {
      // Solo iniciar polling si está en processing y no hay ya uno activo
      if (status === "processing" && !pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(async () => {
          try {
            const response = await apiClient.get(`/leadmagnet/strategy/${uuii}`);
            const data = response.data;

            // Guardar el tenant_id si viene en la respuesta
            if (data.byneural_tenant_id) {
              setTenantId(data.byneural_tenant_id);
            }

            if (data.status === "completed" && data.json_result) {
              setStrategyData(data.json_result);
              setStatus("completed");
              setLoading(false);
              // Limpiar polling
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            } else if (data.status === "failed") {
              setError(data.error_message || "Error al generar la estrategia");
              setStatus("failed");
              setLoading(false);
              // Limpiar polling
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            }
          } catch (err) {
            console.error("Error polling strategy:", err);
            // No mostrar error en polling, solo detener
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }, 1000);
      }
    };

    startPolling();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [uuii, status]);

  // Funciones de validación
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Acepta formatos: +34 612345678, 612345678, +34612345678, etc.
    const phoneRegex = /^(\+?\d{1,3}[\s]?)?\d{9,15}$/;
    const cleanPhone = phone.replace(/\s/g, "");
    return phoneRegex.test(cleanPhone) && cleanPhone.replace(/\D/g, "").length >= 9;
  };

  // Función para normalizar URLs añadiendo https:// si es necesario
  const normalizeUrlWithHttps = (url) => {
    if (!url) return "";
    let normalized = url.trim();
    if (!normalized) return "";
    if (normalized.toLowerCase().startsWith("https://")) {
      return normalized;
    }
    if (normalized.toLowerCase().startsWith("http://")) {
      normalized = normalized.slice("http://".length);
    }
    return `https://${normalized.replace(/^\/\//, "")}`;
  };

  // Función para validar que la URL tiene un dominio válido
  const hasValidDomain = (url) => {
    if (!url) return false;
    const withoutProtocol = url.replace(/^https?:\/\//i, "");
    return withoutProtocol.includes(".");
  };

  const validateWebsite = (url) => {
    if (!url || !url.trim()) return true; // Opcional, así que vacío es válido
    // Normalizar URL
    const normalizedUrl = normalizeUrlWithHttps(url);
    // Verificar que tiene un dominio válido (contiene un punto)
    if (!hasValidDomain(normalizedUrl)) {
      return false;
    }
    // Verificar longitud máxima
    if (normalizedUrl.length > 500) {
      return false;
    }
    return true;
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    // Limpiar error del campo cuando el usuario escribe
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleDownloadSubmit = async () => {
    const errors = {
      nombre: "",
      email: "",
      telefono: "",
      empresa: "",
      website: "",
    };
    let hasErrors = false;

    // Validar nombre
    if (!formData.nombre.trim()) {
      errors.nombre = "El nombre es obligatorio";
      hasErrors = true;
    }

    // Validar email
    if (!formData.email.trim()) {
      errors.email = "El email es obligatorio";
      hasErrors = true;
    } else if (!validateEmail(formData.email)) {
      errors.email = "Introduce un email válido";
      hasErrors = true;
    }

    // Validar teléfono
    if (!formData.telefono.trim()) {
      errors.telefono = "El teléfono es obligatorio";
      hasErrors = true;
    } else if (!validatePhone(formData.telefono)) {
      errors.telefono = "Introduce un teléfono válido (mínimo 9 dígitos)";
      hasErrors = true;
    }

    // Validar empresa
    if (!formData.empresa.trim()) {
      errors.empresa = "El nombre de la empresa es obligatorio";
      hasErrors = true;
    }

    // Validar website (opcional, pero si tiene valor debe ser válido)
    if (formData.website.trim() && !validateWebsite(formData.website)) {
      errors.website = "Introduce una URL válida (ej: https://ejemplo.com)";
      hasErrors = true;
    }

    setFormErrors(errors);

    if (!hasErrors) {
      try {
        setSubmittingLead(true);
        setSubmitError(null); // Limpiar error previo

        // Enviar el lead al backend
        await apiClient.post("/leadmagnet/lead/capture/", {
          strategy_id: uuii,
          name: formData.nombre.trim(),
          email: formData.email.trim(),
          phone: formData.telefono.trim(),
          company: formData.empresa.trim(),
          website: formData.website.trim() ? normalizeUrlWithHttps(formData.website) : " ",
        });

        // Si el envío fue exitoso, descargar el PDF
        if (pdfLinkRef.current) {
          pdfLinkRef.current.click();
        }

        // Resetear el formulario
        setFormData({ nombre: "", email: "", telefono: "", empresa: "", website: "" });
        setFormErrors({ nombre: "", email: "", telefono: "", empresa: "", website: "" });
      } catch (error) {
        // Extraer mensaje de error del backend si existe
        let errorMessage = "Hubo un error al procesar tu solicitud. Por favor, intenta nuevamente.";

        const data = error.response?.data;
        if (data) {
          // Detectar error específico de agencia no encontrada
          if (data.byneural_tenant_id) {
            const tenantError = Array.isArray(data.byneural_tenant_id)
              ? data.byneural_tenant_id[0]
              : data.byneural_tenant_id;

            // Mensaje personalizado para cuando no hay agencia configurada
            if (tenantError.includes("No agency found")) {
              errorMessage = "La agencia no tiene configurado su id del asitente de byneural. Por favor, conatcte con el administrador de la agencia para asignarle un id.";
            } else {
              errorMessage = tenantError;
            }
          } else if (data.detail) {
            errorMessage = data.detail;
          } else if (data.message) {
            errorMessage = data.message;
          } else if (typeof data === "object") {
            // Para otros errores de validación de campos (DRF format)
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey])) {
              errorMessage = data[firstKey][0];
            } else if (firstKey && typeof data[firstKey] === "string") {
              errorMessage = data[firstKey];
            }
          }
        }

        setSubmitError(errorMessage);
      } finally {
        setSubmittingLead(false);
      }
    }
  };

  // Si no hay uuii, mostrar estado vacío
  if (!uuii) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <EmptyStateContainer
          title="Acceso no válido"
          message="No se ha proporcionado un identificador válido. Asegúrate de acceder desde el enlace correcto."
          icon={AlertCircle}
          badgeText="ID no encontrado"
          variant="error"
        />
      </Box>
    );
  }

  // Estado de carga
  if (loading || status === "processing") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress
          size={60}
          sx={{ color: theme.palette.primary.main }}
        />
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 500 }}>
          Analizando tu negocio y diseñando tu estrategia…
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: "400px", textAlign: "center" }}
        >
          Este proceso puede tomar algunos segundos. Por favor, no cierres esta
          ventana.
        </Typography>
      </Box>
    );
  }

  // Estado de error
  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <EmptyStateContainer
          title="Error al cargar"
          message={error}
          icon={AlertCircle}
          badgeText="Error"
          variant="error"
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: isEmbeddedInApp ? "relative" : "absolute",
        top: isEmbeddedInApp ? "auto" : 0,
        left: isEmbeddedInApp ? "auto" : 0,
        right: isEmbeddedInApp ? "auto" : 0,
        bottom: isEmbeddedInApp ? "auto" : 0,
        minHeight: isEmbeddedInApp ? "auto" : "100vh",
        overflowY: isEmbeddedInApp ? "visible" : "auto",
        overflowX: "hidden",
        backgroundColor: isEmbeddedInApp
          ? "transparent"
          : theme.palette.background.default,
        padding: isEmbeddedInApp ? 0 : { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: isEmbeddedInApp ? "100%" : "1000px",
          margin: isEmbeddedInApp ? 0 : "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Botón de volver cuando viene desde Leads */}
        {hideForm && !isEmbeddedInApp && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0,
            }}
          >
            <IconButton
              onClick={handleBackNavigation}
              size="small"
              sx={{
                color: "primary.main",
                padding: "4px",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <ChevronLeft size={30} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={handleBackNavigation}
            >
              {backLabel}
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pt: 4,
            pb: 0,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
            }}
          >
            <Sparkles size={28} color="#ffffff" />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            ¡Tu Plan de Marketing Personalizado está Listo!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "600px",
            }}
          >
            Basado en tus respuestas, hemos diseñado una estrategia para
            impulsar tu negocio.
          </Typography>
        </Box>

        {/* Diagnóstico Actual */}
        {strategyData?.diagnostico && (
          <Card
            sx={{
              backgroundColor: `${theme.palette.primary.main}08`,
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  marginBottom: 1,
                }}
              >
                <ClipboardList size={20} color={theme.palette.primary.main} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  Diagnóstico Actual
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  lineHeight: 1.7,
                }}
              >
                {strategyData.diagnostico}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Estrategia Recomendada */}
        {strategyData?.estrategia && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                marginBottom: 3,
              }}
            >
              <Target size={22} color={theme.palette.text.primary} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Estrategia Recomendada: Método SERSEO
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
              }}
            >
              {/* Atracción */}
              {strategyData.estrategia.atraccion && (
                <StrategyCard
                  number="1"
                  title="Atracción"
                  description={strategyData.estrategia.atraccion}
                  theme={theme}
                />
              )}

              {/* Captación */}
              {strategyData.estrategia.captacion && (
                <StrategyCard
                  number="2"
                  title="Captación"
                  description={strategyData.estrategia.captacion}
                  theme={theme}
                />
              )}

              {/* Conversión */}
              {strategyData.estrategia.conversion && (
                <StrategyCard
                  number="3"
                  title="Conversión"
                  description={strategyData.estrategia.conversion}
                  theme={theme}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Pack Recomendado y Beneficios */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
          }}
        >
          {/* Pack Recomendado */}
          {normalizedPack && (
            <Card
              sx={{
                backgroundColor: `${theme.palette.primary.main}08`,
                border: `1px solid ${theme.palette.primary.light}40`,
              }}
            >
              <CardContent sx={{ padding: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    marginBottom: 2,
                  }}
                >
                  <Package size={20} color={theme.palette.primary.main} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Pack SERSEO Recomendado
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    marginBottom: 0.5,
                  }}
                >
                  {normalizedPack.nombre || "Pack Recomendado"}
                </Typography>

                {normalizedPack.precio && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                      marginBottom: 2,
                    }}
                  >
                    {normalizedPack.precio}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    lineHeight: 1.6,
                  }}
                >
                  {normalizedPack.descripcion ||
                    normalizedPack.justificacion ||
                    normalizedPack}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Beneficios Esperados */}
          {strategyData?.beneficios && strategyData.beneficios.length > 0 && (
            <Card
              sx={{
                backgroundColor: `${theme.palette.success.main}08`,
                border: `1px solid ${theme.palette.success.light}40`,
              }}
            >
              <CardContent sx={{ padding: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    marginBottom: 2,
                  }}
                >
                  <TrendingUp size={20} color={theme.palette.success.main} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Beneficios Esperados
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {strategyData.beneficios.map((beneficio, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <CheckCircle2
                        size={18}
                        color={theme.palette.success.main}
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.5,
                        }}
                      >
                        {beneficio}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Footer con formulario */}
        <Box
          sx={{
            textAlign: "center",
            paddingBottom: 4,
            paddingTop: 2,
          }}
        >
          {/* Formulario de descarga PDF */}
          {strategyData && hideForm && returnTo === "plan-marketing" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                marginBottom: 3,
              }}
            >
              <PDFDownloadLink
                document={
                  <LeadmagnetPDF
                    strategyData={strategyData}
                    palette={pdfPalette}
                  />
                }
                fileName="plan-marketing-serseo.pdf"
                style={{ textDecoration: "none" }}
              >
                {({ loading: pdfLoading }) => (
                  <Button
                    variant="outline"
                    disabled={pdfLoading}
                  >
                    <Download size={16} />
                    {pdfLoading ? "Preparando PDF..." : "Descargar PDF"}
                  </Button>
                )}
              </PDFDownloadLink>
            </Box>
          )}

          {strategyData && !hideForm && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                marginBottom: 3,
              }}
            >
              {/* PDFDownloadLink oculto - se activa programáticamente */}
              <PDFDownloadLink
                document={
                  <LeadmagnetPDF
                    strategyData={strategyData}
                    palette={pdfPalette}
                  />
                }
                fileName="plan-marketing-serseo.pdf"
                style={{ display: "none" }}
              >
                {({ url }) => (
                  <a
                    ref={pdfLinkRef}
                    href={url}
                    download="plan-marketing-serseo.pdf"
                    style={{ display: "none" }}
                  >
                    Download
                  </a>
                )}
              </PDFDownloadLink>

              {/* Formulario */}
              <Box sx={{ width: "100%", maxWidth: "550px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Download size={22} color={theme.palette.text.primary} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Descarga tu Plan Personalizado
                  </Typography>
                </Box>
                <Card
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <CardContent sx={{ padding: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, mb: 1 }}
                      >
                        Completa tus datos para descargar el PDF con tu plan de marketing
                        personalizado.
                      </Typography>

                      {/* Campos Nombre y Teléfono en horizontal */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        {/* Campo Nombre */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.5,
                              color: theme.palette.text.primary,
                              textAlign: "left",
                            }}
                          >
                            Nombre *
                          </Typography>
                          <CustomTextField
                            placeholder="Tu nombre"
                            value={formData.nombre}
                            onChange={handleFormChange("nombre")}
                            error={!!formErrors.nombre}
                            errorMessage={formErrors.nombre}
                          />
                        </Box>

                        {/* Campo Teléfono */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.5,
                              color: theme.palette.text.primary,
                              textAlign: "left",
                            }}
                          >
                            Teléfono *
                          </Typography>
                          <CustomTextField
                            type="tel"
                            placeholder="+34 612 345 678"
                            value={formData.telefono}
                            onChange={handleFormChange("telefono")}
                            error={!!formErrors.telefono}
                            errorMessage={formErrors.telefono}
                          />
                        </Box>
                      </Box>

                      {/* Campos Email y Empresa en horizontal */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        {/* Campo Email */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.5,
                              color: theme.palette.text.primary,
                              textAlign: "left",
                            }}
                          >
                            Email *
                          </Typography>
                          <CustomTextField
                            type="email"
                            placeholder="tu@email.com"
                            value={formData.email}
                            onChange={handleFormChange("email")}
                            error={!!formErrors.email}
                            errorMessage={formErrors.email}
                          />
                        </Box>

                        {/* Campo Empresa */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.5,
                              color: theme.palette.text.primary,
                              textAlign: "left",
                            }}
                          >
                            Nombre de la Empresa *
                          </Typography>
                          <CustomTextField
                            type="text"
                            placeholder="Nombre de tu empresa"
                            value={formData.empresa}
                            onChange={handleFormChange("empresa")}
                            error={!!formErrors.empresa}
                            errorMessage={formErrors.empresa}
                          />
                        </Box>
                      </Box>

                      {/* Campo Página Web (opcional) */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            mb: 0.5,
                            color: theme.palette.text.primary,
                            textAlign: "left",
                          }}
                        >
                          Página Web{" "}
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            (opcional)
                          </Typography>
                        </Typography>
                        <CustomTextField
                          type="url"
                          placeholder="https://ejemplo.com"
                          value={formData.website}
                          onChange={handleFormChange("website")}
                          error={!!formErrors.website}
                          errorMessage={formErrors.website}
                          maxLength={500}
                        />
                      </Box>

                      {/* Mensaje de error general */}
                      {submitError && (
                        <Box
                          sx={{
                            padding: 2,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <AlertCircle
                            size={20}
                            color={theme.palette.error.main}
                            style={{ flexShrink: 0, marginTop: 2 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.error.main,
                              lineHeight: 1.5,
                            }}
                          >
                            {submitError}
                          </Typography>
                        </Box>
                      )}

                      {/* Botón de acción */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 1,
                        }}
                      >
                        <Button
                          variant="default"
                          onClick={handleDownloadSubmit}
                          disabled={submittingLead}
                        >
                          {submittingLead ? (
                            <CircularProgress size={16} sx={{ color: "inherit" }} />
                          ) : (
                            <Download size={16} />
                          )}
                          {submittingLead ? "Enviando..." : "Descargar PDF"}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
          {!hideForm && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              ¿Tienes dudas sobre tu plan? Contáctanos para más información.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Componente para las tarjetas de estrategia
const StrategyCard = ({ number, title, description, theme }) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ padding: 3, height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBottom: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: "1rem",
            }}
          >
            {number}. {title}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default Leadmagnet;
